import { Injectable } from '@angular/core';
import { RemoteIOService } from '../../services/remote-io.service';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, debounceTime, exhaustMap, map, mergeMap, of, share, switchMap, tap, timeout, withLatestFrom } from 'rxjs';
import { select, Store } from '@ngrx/store';
import { AppState } from '../app.state';
import { RemoteIOActions } from '../states/remote-io/remote-io.action-types';
import * as SharedActions from '../states/shared/shared.actions';
import { UtilsService } from '../../edge/utils.service';
import { RemoteIOModels } from '@models/remote-io.models';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmDialogModel } from '@ui';
import { ConfirmModalModel } from '../../services/confirm-dialogs/confirm-modal.model';

@Injectable()
export class RemoteIOEffects {

  public getRemoteIOList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RemoteIOActions.getRemoteIOs),
      withLatestFrom(
        this.store$.pipe(select(state => state.remoteIOState)),
        this.store$.pipe(select(state => state.locationEditState))),
      switchMap(([, { filters }, { selectedLocationId }]) => {
        return this.remoteIOService
          .getAll({
            locationId: selectedLocationId,
            ...filters,
          })
          .pipe(
            switchMap(res => {
              return [
                RemoteIOActions.getRemoteIOsSuccess({ documents: res }),
              ];
            }),
            catchError(response => {
              return [
                RemoteIOActions.getRemoteIOsFail(),
              ];
            }),
            share(),
          );
      }),
    ),
  );

  public setFilter$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RemoteIOActions.setQueryStringFilter),
      debounceTime(400),
      exhaustMap(() => [RemoteIOActions.resetEntities(), RemoteIOActions.getRemoteIOs()]),
    ),
  );

  public startSaveRemoteIO$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RemoteIOActions.startSaveRemoteIO),
      switchMap(({ document, isEdit }) => [
        RemoteIOActions.setIsSaving({ isSaving: true }),
        RemoteIOActions.saveRemoteIORequest({ document, isEdit }),
      ]),
    ),
  );

  public saveRemoteIORequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RemoteIOActions.saveRemoteIORequest),
      withLatestFrom(
        this.store$.pipe(select(state => state.locationEditState)),
      ),
      switchMap(([{ document, isEdit }, { selectedLocationId }]) => {
        return this.remoteIOSaveService(isEdit, selectedLocationId, { ...document, name: document.name })
          .pipe(
            switchMap((res) => {
              return [
                RemoteIOActions.setIsSaving({ isSaving: false }),
                RemoteIOActions.saveRemoteIORequestSuccess(),
                SharedActions.showMessage({
                  success: this.translateService.instant(
                    isEdit ? 'TOASTS.ENTITY_UPDATED_SUCCESS' : 'TOASTS.ENTITY_CREATED_SUCCESS',
                    { entity: this.translateService.instant('DEVICE_LIST.TOOLTIPS.IOS_REMOTE') }
                  )
                }),
              ];
            }),
            catchError(response => {
              return [
                RemoteIOActions.setIsSaving({ isSaving: false }),
                SharedActions.showMessage({ error: this.utilsService.errMessage(response) }),
                RemoteIOActions.saveRemoteIORequestFail(),
              ];
            }),
          );
      }),
      share(),
    ),
  );

  public startDeleteDevice$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RemoteIOActions.startDeleteRemoteIO),
      debounceTime(400),
      exhaustMap(({ remoteIOId }) => [RemoteIOActions.deleteRemoteIO({ remoteIOId })]),
    ),
  );

  public deleteDevice$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RemoteIOActions.deleteRemoteIO),
      withLatestFrom(
        this.store$.pipe(select(state => state.locationEditState))),
      switchMap(([{ remoteIOId }, { selectedLocationId }]) => {
        return this.remoteIOService
          .delete(selectedLocationId, remoteIOId)
          .pipe(
            switchMap(res => {
              return [
                SharedActions.showMessage({ success: this.translateService.instant('TOASTS.ENTITY_HAS_BEEN_REMOVED', { entity: this.translateService.instant('DEVICE_LIST.TOOLTIPS.IOS_REMOTE') }) }),
                RemoteIOActions.deleteRemoteIOSuccess({ remoteIOId }),
              ];
            }),
            catchError(response => {
              const err = this.utilsService.errMessage(response);
              return [
                RemoteIOActions.deleteRemoteIOFail(),
                SharedActions.showMessage({ error: err }),
              ];
            }),
            share(),
          );
      }),
    ),
  );

  public getOneRemoteIO$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RemoteIOActions.getOneRemoteIO),
      switchMap(({ id }) => {
        return this.remoteIOService
          .getOne(id)
          .pipe(
            switchMap(res => {
              return [
                RemoteIOActions.getOneRemoteIOSuccess({ document: res }),
              ];
            }),
            catchError(response => {
              return [
                RemoteIOActions.getOneRemoteIOFail(),
              ];
            }),
            share(),
          );
      }),
    ),
  );

  public startConfigureRemoteIO$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RemoteIOActions.startConfigureRemoteIO),
      debounceTime(400),
      exhaustMap(({ document }) => [RemoteIOActions.saveRemoteIORequest({ document, isEdit: true })]),
    ),
  );

  public testRemoteIO$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RemoteIOActions.testRemoteIO),
      withLatestFrom(this.store$.pipe(select(state => state.locationEditState)), this.store$.pipe(select(state => state.remoteIOState))),
      switchMap(([, { selectedLocationId, selectedLocation }, { selectedRemoteIODocument }]) => {
        const locationId = selectedLocationId;
        const edges = Object.keys(selectedLocation.edges);
        const actions = [];
        for (let edgeId of edges) {
          const request: RemoteIOModels.TestRemoteIORequest = {
            edgeId,
            ...selectedRemoteIODocument,
          };
          actions.push(RemoteIOActions.testRemoteIOSend({ request }));
        }
        return actions;
      }),
      catchError(err => [RemoteIOActions.testRemoteIOError({ errorMsg: err })],
      ),
    ),
  );

  public testRemoteIOSend$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RemoteIOActions.testRemoteIOSend),
      share(),
      mergeMap(({ request }) => {
        return this.remoteIOService.testRemoteIO(request)
          .pipe(
            switchMap(res => {
              return [
                RemoteIOActions.testRemoteIOSuccess(),
              ];
            }),
            catchError(response => {
              return [
                RemoteIOActions.testRemoteIOError({ errorMsg: response.error }),
              ];
            }),
            timeout(10000),
          );
      }),
    ),
  );

  public testRemoteIOSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RemoteIOActions.testRemoteIOSuccess),
      mergeMap(() => {
        return [SharedActions.doNothing()];
      }),
    ),
  );

  public saveRemoteIORequestSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RemoteIOActions.saveRemoteIORequestSuccess),
      mergeMap(() => {
        return [SharedActions.doNothing()];
      }),
    ),
  );

  public deleteRemoteIOSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RemoteIOActions.deleteRemoteIOSuccess),
      mergeMap(() => {
        return [SharedActions.doNothing()];
      }),
    ),
  );

  public showConfirmOnRemove$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(RemoteIOActions.requestRemoveRemoteIo),
        tap(({ remoteIOId, remoteIOName }) => {
          const msgConfirm = this.translateService.instant('REMOTE_IOS.DELETE_REMOTE_IO_MODAL.CONFIRM_MSG', {
            name: remoteIOName,
          });
          const title = this.translateService.instant('REMOTE_IOS.DELETE_REMOTE_IO_MODAL.TITLE', {
            name: remoteIOName,
          });

          this.store$.dispatch(
            SharedActions.showConfirmModal({
              options: {
                type: ConfirmDialogModel.ConfirmDialogType.CONFIRM,
                msg: msgConfirm,
                title,
                confirm: this.translateService.instant('COMMON.YES'),
                disableClose: true,
                params: {
                  type: ConfirmModalModel.Action.removeRemoteIo,
                  remoteIOId,
                },
              },
            })
          );
        })
      ),
    { dispatch: false }
  );

  constructor(
    private remoteIOService: RemoteIOService,
    private actions$: Actions,
    private store$: Store<AppState>,
    private utilsService: UtilsService,
    private translateService: TranslateService) {
  }

  private remoteIOSaveService(isEdit: boolean, selectedLocationId: string, document: Partial<RemoteIOModels.RemoteIODocument>) {
    if (!isEdit) {
      return this.remoteIOService
        .create({
          vendor: document.vendor,
          productType: document.productType,
          numberOfChannels: document.numberOfChannels,
          ipAddress: document.ipAddress,
          username: document.username,
          password: document.password,
          locationId: selectedLocationId,
          name: document.name,
          tags: document.tags ?? [],
          mapConfig: document.mapConfig,
        });
    } else {
      return this.remoteIOService
        .save({
          _id: document._id,
          vendor: document.vendor,
          productType: document.productType,
          numberOfChannels: document.numberOfChannels,
          ipAddress: document.ipAddress,
          username: document.username,
          password: document.password,
          locationId: selectedLocationId,
          name: document.name,
          tags: document.tags ?? [],
          mapConfig: document.mapConfig,
        });
    }
  }
}
